// The log viewer's model (ui-spec §3.4.4): lines from the `log` subresource with `timestamps=true` arrive as
// "<RFC 3339> [r<rank>] [i<index>] message"; merged gang logs carry the rank tag, indexed Jobs the index tag.

export type LogStream = "stdout" | "stderr";

export interface LogLine {
  /** 1-based line number, the target of `#L<n>` deep links. */
  n: number;
  timestamp?: string;
  rank?: number;
  index?: number;
  stream?: LogStream;
  attempt?: string;
  process?: string;
  text: string;
}

export interface LogFilter {
  stream?: LogStream;
  rank?: number;
  index?: number;
  attempt?: string;
  process?: string;
}

export interface LogMatch {
  line: number;
  start: number;
  end: number;
}

const TS = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2}))\s/;
const RANK = /^\[r(\d+)\]\s?/;
const INDEX = /^\[i(\d+)\]\s?/;

/** Parses one raw line; `n` is assigned by the caller in arrival order. */
export function parseLogLine(raw: string, n: number, stream?: LogStream): LogLine {
  let rest = raw.replace(/\r$/, "");
  const line: LogLine = { n, text: "" };
  const ts = TS.exec(rest);
  if (ts) {
    line.timestamp = ts[1];
    rest = rest.slice(ts[0].length);
  }
  const rank = RANK.exec(rest);
  if (rank) {
    line.rank = Number(rank[1]);
    rest = rest.slice(rank[0].length);
  }
  const index = INDEX.exec(rest);
  if (index) {
    line.index = Number(index[1]);
    rest = rest.slice(index[0].length);
  }
  if (stream) line.stream = stream;
  line.text = rest;
  return line;
}

/** Splits a chunk of streamed text into lines, carrying the unterminated tail to the next chunk. */
export function splitChunk(pending: string, chunk: string): { lines: string[]; pending: string } {
  const text = pending + chunk;
  const parts = text.split("\n");
  const tail = parts.pop() ?? "";
  return { lines: parts, pending: tail };
}

export function applyFilter(lines: readonly LogLine[], filter: LogFilter): LogLine[] {
  const active = Object.entries(filter).filter(([, v]) => v !== undefined);
  if (active.length === 0) return lines.slice();
  return lines.filter((l) => active.every(([k, v]) => l[k as keyof LogFilter] === v));
}

// Strips ANSI SGR sequences so search offsets refer to visible text.
// eslint-disable-next-line no-control-regex
const ANSI = /\u001b\[[0-9;]*m/g;

export function visibleText(text: string): string {
  return text.replace(ANSI, "");
}

/** Case-insensitive matches of `query` in the visible text of each line, in line order. */
export function findMatches(lines: readonly LogLine[], query: string): LogMatch[] {
  const q = query.toLowerCase();
  if (!q) return [];
  const out: LogMatch[] = [];
  for (const l of lines) {
    const hay = visibleText(l.text).toLowerCase();
    let from = 0;
    for (;;) {
      const at = hay.indexOf(q, from);
      if (at === -1) break;
      out.push({ line: l.n, start: at, end: at + q.length });
      from = at + Math.max(q.length, 1);
    }
  }
  return out;
}

/** The first stderr line, for the "First error" pill; undefined when the loaded range has none. */
export function firstError(lines: readonly LogLine[]): number | undefined {
  return lines.find((l) => l.stream === "stderr")?.n;
}

/** Reads `#L<n>` from a location hash. */
export function lineFromHash(hash: string): number | undefined {
  const m = /^#L(\d+)$/.exec(hash);
  return m ? Number(m[1]) : undefined;
}

/** Step to the next or previous match index, wrapping around. */
export function stepMatch(current: number, total: number, direction: 1 | -1): number {
  if (total === 0) return -1;
  if (current < 0) return direction === 1 ? 0 : total - 1;
  return (current + direction + total) % total;
}
